import type { ReactNode } from 'react'

import type { PartyColumnId, PartySummary } from '@aeris/contracts'
import {
  MONEY_SCALE,
  ZERO_MONEY,
  formatMoney,
  parseHumanAmount,
} from '@aeris/money'
import { formatDocument } from '@aeris/party'
import { Badge } from '@aeris/ui'

import { formatDate } from '../lib/date-format.ts'
import { PARTY_ROLE_LABELS } from '../lib/party-labels.ts'
import { formatPhone } from '../lib/phone-format.ts'
import type { ColumnRule } from '../lib/table-columns.ts'

import { TagBadges } from './tag-picker.tsx'

/**
 * As colunas da lista de Clientes — F127.
 *
 * O mesmo desenho de `lib/product-columns.tsx`: uma entrada por coluna, com
 * rótulo, quem pode ligar, ordenação e célula. Mora em `components/` porque o
 * nome carrega os marcadores (`TagBadges`), que são componente desta pasta.
 *
 * Quem vê cada coluna segue o dono do dado: limite de crédito com
 * `finance.read` (o mesmo da ficha do cliente), saldo de vale com
 * `exchange.read`, última compra e vendedor com `sales.read`. A rota também não
 * entrega o que o papel não alcança.
 */

/**
 * As colunas por que Clientes sabe se ordenar — F185.
 *
 * Papéis, saldo de vale, vendedor, última compra, telefone e e-mail ficaram de
 * fora; o motivo de cada um está no contrato e no documento da F185.
 */
export type PartySortKey =
  | 'legalName'
  | 'code'
  | 'document'
  | 'kind'
  | 'city'
  | 'creditLimit'
  | 'stateRegistration'
  | 'status'
  | 'createdAt'

export type PartyColumn = ColumnRule<PartyColumnId> & {
  readonly headerClassName?: string
  readonly sortKey?: PartySortKey
  readonly numeric?: boolean
  readonly className?: string
  /** O que a coluna pede ao servidor além do cadastro. */
  readonly include?: 'credit' | 'voucher' | 'activity'
  readonly cell: (party: PartySummary) => ReactNode
}

/** As colunas de antes da F127: quem nunca escolheu vê a lista de sempre. */
export const DEFAULT_PARTY_COLUMNS: readonly PartyColumnId[] = [
  'code',
  'name',
  'document',
  'roles',
  'kind',
]

const dash = <span className="text-(--text-muted)">—</span>

/** Do texto decimal exato — ADR-0016, nunca `Number()`. */
const asMoney = (value: string): string =>
  formatMoney(parseHumanAmount(value, MONEY_SCALE) ?? ZERO_MONEY)

export const PARTY_COLUMNS: readonly PartyColumn[] = [
  {
    id: 'code',
    sortKey: 'code',
    label: 'Cód.',
    requires: [],
    // O código do cliente na loja (F211). Quem veio de planilha guarda o
    // número do sistema antigo — "o 2558" continua o 2558.
    headerClassName: 'w-20',
    className: 'font-(--font-mono) text-(length:--text-xs) text-(--text-muted)',
    cell: (party) =>
      party.code === undefined || party.code === null
        ? (party.externalCode ?? '—')
        : String(party.code),
  },
  {
    id: 'name',
    label: 'Nome',
    requires: [],
    locked: true,
    sortKey: 'legalName',
    cell: (party) => (
      <>
        <span className="text-(--text-primary)">{party.legalName}</span>
        {party.tradeName === null ? null : (
          // Entre parênteses, e não só com margem: sem o CSS, os dois nomes
          // grudariam num só.
          <span className="ml-2 text-(length:--text-xs) text-(--text-muted)">
            ({party.tradeName})
          </span>
        )}
        {party.status === 'archived' ? (
          <Badge tone="neutral" className="ml-2">
            Arquivado
          </Badge>
        ) : null}
        {/* Os marcadores (F088) na célula do nome: a maioria dos cadastros
            não tem nenhum, e uma coluna própria reservaria largura à toa. */}
        <TagBadges tags={party.tags} className="mt-1" />
      </>
    ),
  },
  {
    id: 'document',
    sortKey: 'document',
    label: 'Documento',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap text-(--text-secondary)',
    cell: (party) =>
      party.document === null
        ? '—'
        : formatDocument(party.kind, party.document),
  },
  {
    id: 'roles',
    label: 'Papéis',
    requires: [],
    // Vários selos: quem tem dois papéis é justamente o caso da seção 10.
    cell: (party) => (
      <span className="flex flex-wrap gap-1">
        {party.roles.map((entry) => (
          <Badge key={entry} tone="info">
            {PARTY_ROLE_LABELS[entry]}
          </Badge>
        ))}
      </span>
    ),
  },
  {
    id: 'kind',
    sortKey: 'kind',
    label: 'Tipo',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (party) => (party.kind === 'person' ? 'Física' : 'Jurídica'),
  },
  {
    id: 'phone',
    label: 'Telefone / WhatsApp',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap',
    cell: (party) =>
      party.mainPhone == null ? dash : formatPhone(party.mainPhone),
  },
  {
    id: 'email',
    label: 'E-mail',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (party) => party.mainEmail ?? dash,
  },
  {
    id: 'address',
    label: 'Endereço',
    requires: [],
    className: 'text-(--text-secondary)',
    cell: (party) => party.mainAddress ?? dash,
  },
  {
    id: 'city',
    sortKey: 'city',
    label: 'Cidade / UF',
    requires: [],
    className: 'whitespace-nowrap',
    cell: (party) =>
      party.mainCity == null
        ? dash
        : `${party.mainCity}${party.mainUf == null ? '' : ` / ${party.mainUf}`}`,
  },
  {
    id: 'creditLimit',
    sortKey: 'creditLimit',
    label: 'Limite de crédito',
    requires: ['finance.read'],
    include: 'credit',
    numeric: true,
    cell: (party) =>
      party.creditLimit == null ? dash : asMoney(party.creditLimit),
  },
  {
    id: 'voucherBalance',
    label: 'Saldo de vale',
    requires: ['exchange.read'],
    include: 'voucher',
    numeric: true,
    cell: (party) =>
      party.voucherBalance == null ? dash : asMoney(party.voucherBalance),
  },
  {
    id: 'portfolio',
    label: 'Vendedor do cliente',
    requires: [],
    cell: (party) => party.salespersonName ?? dash,
  },
  {
    id: 'salesperson',
    label: 'Vendedor (última venda)',
    requires: ['sales.read'],
    include: 'activity',
    cell: (party) => party.lastSalespersonName ?? dash,
  },
  {
    id: 'createdAt',
    label: 'Cadastrado em',
    requires: [],
    sortKey: 'createdAt',
    className: 'whitespace-nowrap',
    cell: (party) =>
      party.createdAt == null ? dash : formatDate(party.createdAt),
  },
  {
    id: 'lastPurchaseAt',
    label: 'Última compra',
    requires: ['sales.read'],
    include: 'activity',
    className: 'whitespace-nowrap',
    cell: (party) =>
      party.lastPurchaseAt == null ? dash : formatDate(party.lastPurchaseAt),
  },
  {
    id: 'stateRegistration',
    sortKey: 'stateRegistration',
    label: 'Inscrição estadual',
    requires: [],
    className: 'font-(--font-mono) whitespace-nowrap text-(--text-secondary)',
    cell: (party) => party.stateRegistration ?? '—',
  },
  {
    id: 'status',
    sortKey: 'status',
    label: 'Situação',
    requires: [],
    cell: (party) =>
      party.status === 'archived' ? (
        <Badge tone="neutral">Arquivado</Badge>
      ) : (
        <Badge tone="success">Ativo</Badge>
      ),
  },
]

/** O que pedir ao servidor para as colunas que estão na tela. */
export const partyIncludesFor = (
  columns: readonly PartyColumn[]
): ('credit' | 'voucher' | 'activity')[] => [
  ...new Set(
    columns.flatMap((column) =>
      column.include === undefined ? [] : [column.include]
    )
  ),
]
